import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Gift,
  History,
  Users,
  CheckCircle,
  ArrowRight,
  Loader2,
  Package,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Seo from "@/components/Seo";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = "/api";

const benefits = [
  {
    icon: Gift,
    title: "Free Parts",
    description:
      "When you help us reproduce a part, you receive free reproductions as thanks for your contribution.",
  },
  {
    icon: History,
    title: "Preserve History",
    description:
      "Your collection helps keep historical firearms alive and operational for future generations.",
  },
  {
    icon: Users,
    title: "Help Fellow Collectors",
    description:
      "Make hard-to-find parts available to the community of collectors and enthusiasts.",
  },
];

const howItWorks = [
  "When a customer requests a reproduction and we can't source the specs from our archives, we turn to our Partner Program.",
  "We post a \"wanted\" notice on our online bulletin board for the part we're seeking.",
  "If you have the part, simply loan it to us for a short period.",
  "We'll precisely measure, replicate, and return your original—along with free reproductions!",
];

export default function PartnerPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    collection_description: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/partner-inquiry`, formData);
      setIsSubmitted(true);
      toast.success("Partner inquiry submitted! We'll be in touch soon.");
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        collection_description: "",
      });
    } catch (error) {
      toast.error("Failed to submit inquiry. Please try again.");
      console.error("Partner inquiry error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="partner-page">
      <Seo
        title="Partner Program"
        description="Partner with Resurrected Gun Parts to bring reproduction military firearm parts to your customers."
        path="/partner-program"
      />
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#0f0f0f] border-b border-white/5">
        <div className="container-tactical">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C0A062]/10 border border-[#C0A062]/20 rounded-sm">
                <Users className="w-4 h-4 text-[#C0A062]" />
                <span className="font-mono text-[#C0A062] text-sm uppercase tracking-wider">
                  Partner Program
                </span>
              </div>

              <h1 className="font-heading font-black text-5xl md:text-6xl lg:text-7xl text-white uppercase tracking-tighter">
                Turn Your Collection Into{" "}
                <span className="text-[#C0A062]">Free Parts</span>
              </h1>

              <p className="text-[#A1A1AA] text-lg md:text-xl max-w-2xl mx-auto">
                Have rare or unique historical firearm parts in your collection?
                Put them to work for you and help preserve firearm history.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-spacing bg-[#0a0a0a]" data-testid="benefits-section">
        <div className="container-tactical">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-mono text-[#C0A062] text-sm tracking-[0.3em] uppercase mb-4">
              Why Join?
            </p>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white uppercase tracking-tight">
              Program Benefits
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, i) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center p-8 bg-[#121212] border border-white/5 hover:border-[#C0A062]/30 transition-colors"
                data-testid={`benefit-${i}`}
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-sm bg-[#C0A062]/10 flex items-center justify-center">
                  <benefit.icon className="w-8 h-8 text-[#C0A062]" />
                </div>
                <h3 className="font-heading font-bold text-white text-xl uppercase tracking-tight mb-3">
                  {benefit.title}
                </h3>
                <p className="text-[#A1A1AA] leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-spacing bg-[#0f0f0f]" data-testid="how-it-works">
        <div className="container-tactical">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="font-mono text-[#C0A062] text-sm tracking-[0.3em] uppercase mb-4">
                The Process
              </p>
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-white uppercase tracking-tight mb-8">
                How It Works
              </h2>

              <div className="space-y-6">
                {howItWorks.map((step, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex gap-4"
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-sm bg-[#C0A062] flex items-center justify-center">
                      <span className="font-heading font-bold text-black text-sm">
                        {i + 1}
                      </span>
                    </div>
                    <p className="text-[#A1A1AA] leading-relaxed pt-1">{step}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square rounded-sm overflow-hidden bg-[#121212] border border-white/5 p-12 flex flex-col items-center justify-center text-center">
                <Package className="w-24 h-24 text-[#C0A062] mb-6" />
                <h3 className="font-heading font-bold text-white text-2xl uppercase tracking-tight mb-2">
                  Your Parts
                </h3>
                <p className="text-[#A1A1AA]">
                  Safely returned with free reproductions
                </p>
                <div className="mt-8 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#8CB369]" />
                  <span className="text-[#8CB369] font-mono text-sm uppercase">
                    Insured & Protected
                  </span>
                </div>
              </div>
              {/* Decorative */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#C0A062]/10 rounded-sm border border-[#C0A062]/20 -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Sign Up Form */}
      <section className="section-spacing bg-[#0a0a0a]" data-testid="partner-signup">
        <div className="container-tactical">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <p className="font-mono text-[#C0A062] text-sm tracking-[0.3em] uppercase mb-4">
                Get Started
              </p>
              <h2 className="font-heading font-bold text-4xl md:text-5xl text-white uppercase tracking-tight mb-4">
                Join the Program
              </h2>
              <p className="text-[#A1A1AA]">
                Tell us about your collection and we'll be in touch about
                partnership opportunities.
              </p>
            </motion.div>

            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-16 bg-[#121212] border border-white/5 rounded-sm"
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#4A5D23]/20 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-[#8CB369]" />
                </div>
                <h3 className="font-heading font-bold text-white text-2xl uppercase mb-2">
                  Inquiry Received!
                </h3>
                <p className="text-[#A1A1AA] mb-6">
                  We'll review your information and get back to you soon.
                </p>
                <Button
                  onClick={() => setIsSubmitted(false)}
                  className="btn-secondary"
                >
                  Submit Another
                </Button>
              </motion.div>
            ) : (
              <motion.form
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                onSubmit={handleSubmit}
                className="bg-[#121212] border border-white/5 rounded-sm p-8 space-y-6"
                data-testid="partner-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label
                      htmlFor="first_name"
                      className="text-[#A1A1AA] font-mono text-xs uppercase tracking-wider"
                    >
                      First Name *
                    </Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                      className="input-tactical input-glow"
                      data-testid="partner-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="last_name"
                      className="text-[#A1A1AA] font-mono text-xs uppercase tracking-wider"
                    >
                      Last Name *
                    </Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                      className="input-tactical input-glow"
                      data-testid="partner-last-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="email"
                    className="text-[#A1A1AA] font-mono text-xs uppercase tracking-wider"
                  >
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="input-tactical input-glow"
                    data-testid="partner-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="phone"
                    className="text-[#A1A1AA] font-mono text-xs uppercase tracking-wider"
                  >
                    Phone (Optional)
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="input-tactical input-glow"
                    data-testid="partner-phone"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="collection_description"
                    className="text-[#A1A1AA] font-mono text-xs uppercase tracking-wider"
                  >
                    Tell Us About Your Collection *
                  </Label>
                  <Textarea
                    id="collection_description"
                    name="collection_description"
                    value={formData.collection_description}
                    onChange={handleChange}
                    required
                    className="input-tactical input-glow min-h-[150px] resize-none"
                    placeholder="Describe the vintage firearms and parts in your collection..."
                    data-testid="partner-collection"
                  />
                </div>

                <Button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={isSubmitting}
                  data-testid="partner-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <span>Join Partner Program</span>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </motion.form>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#121212] border-t border-white/5">
        <div className="container-tactical text-center">
          <h3 className="font-heading font-bold text-2xl text-white uppercase tracking-tight mb-4">
            Have Questions?
          </h3>
          <p className="text-[#A1A1AA] mb-6">
            Want to learn more about how the Partner Program works? Get in
            touch.
          </p>
          <Link to="/about" data-testid="partner-contact-cta">
            <Button className="btn-secondary">
              <span>Contact Us</span>
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
